import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Typography,
  Button,
  InputAdornment,
  Pagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { fetchDemarches, deleteDemarche } from '../redux/slices/demarchesSlice';
import { AppDispatch, RootState } from '../redux/store';
import Layout from '../components/Layout';
import DrawerPanel from '../components/DrawerPanel';

const DemarchesList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { demarches, loading, pagination } = useSelector((state: RootState) => state.demarches);

  const [search, setSearch] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchDemarches({
      page,
      limit: 20,
      search,
      statut: statutFilter,
    }));
  }, [dispatch, page, search, statutFilter]);

  const getClientName = (demarche: any) => {
    return demarche.type_client === 'entreprise'
      ? demarche.raison_sociale
      : `${demarche.client_nom} ${demarche.client_prenom || ''}`;
  };

  const getStatutColor = (statut: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    const statutColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
      'en_attente': 'warning',
      'en_cours': 'info',
      'termine': 'success',
      'annule': 'error',
    };
    return statutColors[statut] || 'default';
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette démarche ?')) {
      await dispatch(deleteDemarche(id));
      dispatch(fetchDemarches({ page, limit: 20, search, statut: statutFilter }));
    }
  };

  return (
    <Layout>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Démarches
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDrawerOpen(true)}
        >
          Nouvelle démarche
        </Button>
      </Box>

      {/* Filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher une démarche..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            label="Statut"
            value={statutFilter}
            onChange={(e) => setStatutFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Tous les statuts</MenuItem>
            <MenuItem value="en_attente">En attente</MenuItem>
            <MenuItem value="en_cours">En cours</MenuItem>
            <MenuItem value="termine">Terminé</MenuItem>
            <MenuItem value="annule">Annulé</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>Client</TableCell>
              <TableCell>Titre</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Date dépôt</TableCell>
              <TableCell>Sous-démarches</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  Chargement...
                </TableCell>
              </TableRow>
            ) : !demarches || demarches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">
                    Aucune démarche trouvée
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              (demarches || []).map((demarche: any) => (
                <TableRow
                  key={demarche.id}
                  sx={{
                    backgroundColor: demarche.necessite_intervention ? '#fff3cd' : 'inherit',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                  }}
                >
                  <TableCell>
                    <Link
                      to={`/clients/${demarche.client_id}`}
                      style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 500 }}
                    >
                      {getClientName(demarche)}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {demarche.necessite_intervention && (
                        <WarningIcon color="warning" fontSize="small" />
                      )}
                      <Typography variant="body2">{demarche.titre}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={demarche.type_demarche_nom || 'Non spécifié'}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={demarche.statut}
                      size="small"
                      color={getStatutColor(demarche.statut)}
                    />
                  </TableCell>
                  <TableCell>
                    {demarche.date_depot || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={demarche.nb_sous_demarches || 0}
                      size="small"
                      color={demarche.nb_sous_demarches > 0 ? 'info' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <IconButton
                        component={Link}
                        to={`/demarches/${demarche.id}`}
                        size="small"
                        color="primary"
                        title="Voir détails"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(demarche.id)}
                        title="Supprimer"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 2 }}>
          <Pagination
            count={pagination.totalPages}
            page={page}
            onChange={(e, value) => {
              setPage(value);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
          <Typography variant="body2" color="text.secondary">
            Page {pagination.page} sur {pagination.totalPages} ({pagination.total} démarche{pagination.total > 1 ? 's' : ''} au total)
          </Typography>
        </Box>
      )}

      {/* Drawer pour création démarche */}
      <DrawerPanel
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Nouvelle démarche"
        width={700}
      >
        <Typography>Formulaire de création de démarche à venir...</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Pour créer une démarche, allez d'abord sur le profil d'un client.
        </Typography>
      </DrawerPanel>
    </Layout>
  );
};

export default DemarchesList;
